import { Option, Select } from "@mui/joy";
import axios from "axios";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ClearButton from "common/components/Button/ClearButton";
import { useAuth } from "common/hooks/useAuth";
import { BACKEND_URL_CONFIG } from "config";
import { ERRORS } from "errors";
import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import styles from "./NgramFrequenciesForm.module.css";

const customSelectStyle = {
  "--Select-placeholderOpacity": "unset",
  backgroundColor: "white",
  fontSize: "13px",
  fontFamily: "inherit",
};

export default function NgramFrequenciesForm({
  block,
  formId,
  needAdvancedOptions,
  setFormData,
  setIsSubmitting,
  defaultBlockDisplayName,
}) {
  // States
  const [loading, setLoading] = useState(false);
  const [availableColumnsInfo, setAvailableColumnsInfo] = useState(null);

  // Contexts
  const { currentUser } = useAuth();
  const { versionId } = useParams();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = useForm({
    defaultValues: {
      blockDisplayName: block ? block.name : defaultBlockDisplayName,
      max_ngrams: block ? block.parameters.max_ngrams : 10,
      blacklist: block
        ? block.parameters.blacklist.map((item) => ({
            value: item,
            label: item,
          }))
        : [],
      language_column_id: block ? block.parameters.language_column_id : null,
      verbatim_column_id: block ? block.parameters.verbatim_column_id : null,
      segmenting_column_ids: block
        ? block.parameters.segmenting_column_ids
        : [],
    },
  });
  const fetchAvailableColumns = useCallback(async () => {
    setLoading(true);
    const url = BACKEND_URL_CONFIG.getAvailableColumnsInfo.replace(
      "{versionId}",
      versionId
    );
    try {
      const response = await axios.get(url, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response.status === 200) {
        setAvailableColumnsInfo(response.data);
      }
    } catch (error) {
      toast.error(ERRORS.error_130);
    } finally {
      setLoading(false);
    }
  }, [currentUser, versionId]);

  useEffect(() => {
    fetchAvailableColumns();
  }, [fetchAvailableColumns]);

  useEffect(() => {
    setIsSubmitting(isSubmitting);
  }, [isSubmitting, setIsSubmitting]);

  const onSubmit = (data) => {
    const submittedData = {
      parameters: {
        blacklist: data.blacklist.map((item) => item.value),
        max_ngrams: parseInt(data.max_ngrams),
        segmenting_column_ids: data.segmenting_column_ids,
        language_column_id: data.language_column_id,
        language_column_id_set_by_user: !!data.language_column_id,
        verbatim_column_id: data.verbatim_column_id,
        verbatim_column_id_set_by_user: !!data.verbatim_column_id,
      },
      blockDisplayName: data.blockDisplayName,
    };

    setFormData(submittedData);
  };

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      className={styles.container}
    >
      <div className={styles["field-container"]}>
        <label className={styles.label}>Nom du bloc</label>
        <input
          {...register("blockDisplayName", { required: true })}
          type="text"
          required={true}
          className={styles["text-input"]}
          placeholder="Saisir le nom de votre bloc"
        />
      </div>
      <div className={styles["field-container"]}>
        <label className={styles.label}>Maximum ngram</label>
        <input
          {...register("max_ngrams", { required: true })}
          className={styles["text-input"]}
          type="number"
          placeholder="Saisir le nombre maximum de ngram..."
        />
      </div>
      <div className={styles["field-container"]}>
        <label className={styles.label}>Blacklist</label>
        <Controller
          name="blacklist"
          control={control}
          render={({ field }) => (
            <CreatableSelect
              {...field}
              isMulti
              onChange={(value) => {
                field.onChange(value);
              }}
            />
          )}
        />
      </div>
      <div className={styles["field-container"]}>
        <label className={styles.label}>Colonnes segmentantes</label>
        <Controller
          name="segmenting_column_ids"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              multiple
              placeholder="Choisir les colonnes segmentantes..."
              style={customSelectStyle}
              disabled={loading}
              onChange={(e, newValue) => {
                field.onChange(newValue);
              }}
            >
              {Object.entries(availableColumnsInfo || {}).map(
                ([columnId, columnName]) => (
                  <Option key={columnId} value={columnId}>
                    {columnName}
                  </Option>
                )
              )}
            </Select>
          )}
        />
      </div>
      {needAdvancedOptions && (
        <>
          <div className={styles["field-container"]}>
            <label className={styles.label}>Colonne des verbatims</label>
            <Controller
              name="verbatim_column_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Choisir la colonne des verbatims..."
                  style={customSelectStyle}
                  disabled={loading}
                  onChange={(e, newValue) => {
                    field.onChange(newValue);
                  }}
                  {...(field.value && {
                    endDecorator: (
                      <ClearButton
                        onClick={() => {
                          field.onChange(null);
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </ClearButton>
                    ),
                    indicator: null,
                  })}
                >
                  {Object.entries(availableColumnsInfo || {}).map(
                    ([columnId, columnName]) => (
                      <Option key={columnId} value={columnId}>
                        {columnName}
                      </Option>
                    )
                  )}
                </Select>
              )}
            />
          </div>
          <div className={styles["field-container"]}>
            <label className={styles.label}>Colonne de langue à choisir</label>
            <Controller
              name="language_column_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Choisir la colonne de langue..."
                  style={customSelectStyle}
                  disabled={loading}
                  onChange={(e, newValue) => {
                    field.onChange(newValue);
                  }}
                  {...(field.value && {
                    endDecorator: (
                      <ClearButton
                        onClick={() => {
                          field.onChange(null);
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </ClearButton>
                    ),
                    indicator: null,
                  })}
                >
                  {Object.entries(availableColumnsInfo || {}).map(
                    ([columnId, columnName]) => (
                      <Option key={columnId} value={columnId}>
                        {columnName}
                      </Option>
                    )
                  )}
                </Select>
              )}
            />
          </div>
        </>
      )}
    </form>
  );
}
